import { SafeImage } from '@/ui/util/safe-image';
import { validateImage } from '@lib';
import { imageMetaConverter } from '@lib/server';
import { APIImageType } from '@types';
import { clsx } from 'clsx';

import { CardProps } from '.';
import { DesktopRatios, MobileRatios } from '../../../lib/constants/desktop-ratios';

export function CardImage({
  image,
  Wrapper,
  href,
  ratio,
  classNames,
  preserveAspectRatio,
  imageFit,
  children,
}: CardProps & {
  children: React.ReactNode;
  image: APIImageType | Array<APIImageType> | null | undefined;
  Wrapper: React.ElementType;
}) {
  if (!validateImage(image)) {
    return null;
  }
  const imageMeta = imageMetaConverter(image, {}, { includeDimensions: true });

  const style = imageMeta!.width
    ? {
        aspectRatio: (imageMeta!.width as number) / (imageMeta!.height as number),
      }
    : {};

  return (
    image && (
      <Wrapper
        {...((href ? { href } : {}) as React.ComponentProps<typeof Wrapper>)}
        className={clsx(
          'relative order-2 w-full overflow-hidden rounded-lg transition ease-out',
          {
            'min-h-[90vw] sm:min-h-96': !ratio?.mobile,
            'sm:min-h-96': !ratio?.desktop,
            'hover:shadow-lg': href,
          },
          DesktopRatios.get(ratio?.desktop || '') || 'sm:aspect-auto',
          MobileRatios.get(ratio?.mobile || '') || 'aspect-square',
          classNames?.image,
        )}
        data-component="card-image"
      >
        {preserveAspectRatio && (
          <div className="absolute inset-0 z-10 flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-lg bg-black/25 p-4 backdrop-blur-xl">
            <div style={style} className="relative max-h-full">
              <SafeImage
                src={image}
                sizes="(max-width: 1536px) 50vw, (max-with: 648px) 100vw, 768px"
                style={style}
                className="relative overflow-hidden rounded object-cover shadow-sm"
              />
            </div>
          </div>
        )}

        <SafeImage
          src={image}
          sizes={preserveAspectRatio ? '256px' : '(max-width: 1536px) 50vw, (max-with: 648px) 100vw, 768px'}
          width={undefined}
          height={undefined}
          className={clsx(imageFit, 'transition group-hover/card:scale-110')}
          fill
        />
        {children}
      </Wrapper>
    )
  );
}
